// src/pages/PodcastAnalyticsPage.js
import { useState, useEffect } from 'react';
import { 
  collection, 
  collectionGroup,
  getDocs, 
  query, 
  where, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../../firebase';
import AnalyticsService from '../../services/AnalyticsService';

// Debug: Check what's actually imported
console.log('AnalyticsService object:', AnalyticsService);
console.log('Available methods:', Object.getOwnPropertyNames(AnalyticsService));
console.log('getPodcastAnalytics type:', typeof AnalyticsService.getPodcastAnalytics);
console.log('getPodcastAnalytics exists:', 'getPodcastAnalytics' in AnalyticsService);

const PodcastAnalyticsPage = () => {
  const [podcasts, setPodcasts] = useState([]);
  const [filteredPodcasts, setFilteredPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedImportStatus, setSelectedImportStatus] = useState('');
  const [selectedCreator, setSelectedCreator] = useState('');
  
  // Available filter options
  const [genres, setGenres] = useState([]);
  const [creators, setCreators] = useState([]);
  const [podcastAnalytics, setPodcastAnalytics] = useState({});

  useEffect(() => {
    fetchPodcasts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [podcasts, searchTerm, selectedGenre, selectedImportStatus, selectedCreator]);

  const fetchPodcasts = async () => {
    try {
      setLoading(true);
      
      // Fetch all podcasts
      const podcastsSnapshot = await getDocs(collection(db, 'podcasts'));
      const podcastsList = [];
      const genreSet = new Set();
      const creatorSet = new Set();

      for (const doc of podcastsSnapshot.docs) {
        const podcastData = {
          id: doc.id,
          ...doc.data()
        };

        // Collect genres and creators for filters
        if (podcastData.category) {
          genreSet.add(podcastData.category);
        }
        if (podcastData.creator) {
          creatorSet.add(podcastData.creator);
        }

        // Get episode count
        const episodeCount = getEpisodeCount(podcastData);
        
        // Fetch analytics for this podcast
        const analytics = await AnalyticsService.getPodcastAnalytics(doc.id);
        
        podcastsList.push({
          ...podcastData,
          episodeCount,
          analytics
        });
      }

      setPodcasts(podcastsList);
      setGenres(Array.from(genreSet).sort());
      setCreators(Array.from(creatorSet).sort());
      
    } catch (err) {
      console.error('Error fetching podcasts:', err);
      setError('Failed to load podcasts: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getEpisodeCount = (podcast) => {
    if (podcast.episodes) {
      if (Array.isArray(podcast.episodes)) {
        return podcast.episodes.length;
      } else if (typeof podcast.episodes === 'object') {
        return Object.keys(podcast.episodes).length;
      }
    }
    return 0;
  };

  const getImportStatus = (podcast) => {
    // Check if podcast has importStatus field
    if (podcast.importStatus) {
      switch (podcast.importStatus) {
        case 'success': return 'imported';
        case 'partial': return 'partial import';
        case 'failed': return 'failed import';
        default: return 'imported';
      }
    }
    
    // Fallback logic for older podcasts
    if (podcast.category === 'Imported' || !podcast.importDetails) {
      return 'imported';
    }
    
    return 'imported';
  };

  const getImportStatusBadge = (status) => {
    switch (status) {
      case 'imported':
        return <span className="status-badge status-success">✅ Imported</span>;
      case 'partial import':
        return <span className="status-badge status-warning">⚠️ Partial Import</span>;
      case 'failed import':
        return <span className="status-badge status-error">❌ Failed Import</span>;
      default:
        return <span className="status-badge status-neutral">📝 Unknown</span>;
    }
  };

  const applyFilters = () => {
    let filtered = [...podcasts];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(podcast =>
        podcast.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        podcast.creator?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        podcast.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Genre filter
    if (selectedGenre) {
      filtered = filtered.filter(podcast => podcast.category === selectedGenre);
    }

    // Import status filter
    if (selectedImportStatus) {
      filtered = filtered.filter(podcast => getImportStatus(podcast) === selectedImportStatus);
    }

    // Creator filter
    if (selectedCreator) {
      filtered = filtered.filter(podcast => podcast.creator === selectedCreator);
    }

    setFilteredPodcasts(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedGenre('');
    setSelectedImportStatus('');
    setSelectedCreator('');
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: 'DKK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-slate-300">Loading podcast analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-600/10 border border-red-400 rounded-lg p-6">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">❌</span>
          <div>
            <h3 className="text-lg font-semibold text-white">Error Loading Data</h3>
            <p className="text-red-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Podcast Analytics</h1>
          <p className="text-slate-400 mt-1">
            Showing {filteredPodcasts.length} of {podcasts.length} podcasts
          </p>
        </div>
        
        <button
          onClick={fetchPodcasts}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
        >
          <span>🔄</span>
          <span>Refresh Data</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by title, creator..."
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Genre */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Genre
            </label>
            <select
              value={selectedGenre}
              onChange={(e) => setSelectedGenre(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Genres</option>
              {genres.map(genre => (
                <option key={genre} value={genre}>{genre}</option>
              ))}
            </select>
          </div>

          {/* Import Status */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Import Status
            </label>
            <select
              value={selectedImportStatus}
              onChange={(e) => setSelectedImportStatus(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="imported">Imported</option>
              <option value="partial import">Partial Import</option>
              <option value="failed import">Failed Import</option>
            </select>
          </div>

          {/* Creator */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Creator
            </label>
            <select
              value={selectedCreator}
              onChange={(e) => setSelectedCreator(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Creators</option>
              {creators.map(creator => (
                <option key={creator} value={creator}>{creator}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Clear Filters */}
        {(searchTerm || selectedGenre || selectedImportStatus || selectedCreator) && (
          <button
            onClick={clearFilters}
            className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
          >
            Clear all filters
          </button>
        )}
      </div>

      {/* Podcasts Grid */}
      {filteredPodcasts.length === 0 ? (
        <div className="bg-slate-800 rounded-lg p-12 border border-slate-700 text-center">
          <div className="text-6xl mb-4">🎙️</div>
          <h3 className="text-xl font-semibold text-white mb-2">No podcasts found</h3>
          <p className="text-slate-400">
            {podcasts.length === 0 
              ? "No podcasts have been created yet" 
              : "Try adjusting your filters to see more results"
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredPodcasts.map(podcast => (
            <div key={podcast.id} className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden hover:border-slate-600 transition-colors">
              {/* Podcast Header */}
              <div className="p-6 pb-4">
                <div className="flex items-start space-x-4">
                  {/* Podcast Image */}
                  <div className="w-16 h-16 rounded-lg bg-slate-700 flex-shrink-0 overflow-hidden">
                    {podcast.imageURL ? (
                      <img 
                        src={podcast.imageURL} 
                        alt={podcast.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl">
                        🎙️
                      </div>
                    )}
                  </div>

                  {/* Podcast Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white truncate" title={podcast.title}>
                      {podcast.title || 'Untitled Podcast'}
                    </h3>
                    <p className="text-sm text-slate-400 truncate" title={podcast.creator}>
                      by {podcast.creator || 'Unknown Creator'}
                    </p>
                    <div className="flex items-center space-x-2 mt-2">
                      {getImportStatusBadge(getImportStatus(podcast))}
                      <span className="text-xs bg-slate-700 text-slate-300 px-2 py-1 rounded">
                        {podcast.category || 'Uncategorized'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Analytics Stats */}
              <div className="px-6 pb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-700/50 rounded p-3">
                    <div className="text-lg font-semibold text-white">
                      {formatDuration(podcast.analytics?.totalListeningTime)}
                    </div>
                    <div className="text-xs text-slate-400">Total Listening</div>
                  </div>
                  
                  <div className="bg-slate-700/50 rounded p-3">
                    <div className="text-lg font-semibold text-green-400">
                      {formatCurrency(podcast.analytics?.totalEarnings)}
                    </div>
                    <div className="text-xs text-slate-400">Revenue</div>
                  </div>
                  
                  <div className="bg-slate-700/50 rounded p-3">
                    <div className="text-lg font-semibold text-blue-400">
                      {podcast.analytics?.totalListeners || 0}
                    </div>
                    <div className="text-xs text-slate-400">Listeners</div>
                  </div>
                  
                  <div className="bg-slate-700/50 rounded p-3">
                    <div className="text-lg font-semibold text-purple-400">
                      {podcast.episodeCount}
                    </div>
                    <div className="text-xs text-slate-400">Episodes</div>
                  </div>
                </div>

                {/* Top Episodes Preview */}
                {podcast.analytics?.totalListeners > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <div className="text-sm text-slate-400 mb-2">Performance</div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-300">Avg. listening time:</span>
                        <span className="text-slate-400">
                          {formatDuration(podcast.analytics.averageListeningTime)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-300">Revenue per listener:</span>
                        <span className="text-green-400">
                          {formatCurrency(
                            podcast.analytics.totalListeners > 0 
                              ? podcast.analytics.totalEarnings / podcast.analytics.totalListeners 
                              : 0
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {filteredPodcasts.length > 0 && (
        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4">Platform Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-400">
                {filteredPodcasts.length}
              </div>
              <div className="text-sm text-slate-400">Total Podcasts</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-400">
                {filteredPodcasts.reduce((sum, p) => sum + p.episodeCount, 0)}
              </div>
              <div className="text-sm text-slate-400">Total Episodes</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-green-400">
                {formatCurrency(
                  filteredPodcasts.reduce((sum, p) => sum + (p.analytics?.totalEarnings || 0), 0)
                )}
              </div>
              <div className="text-sm text-slate-400">Total Revenue</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-400">
                {new Set(
                  filteredPodcasts
                    .filter(p => p.analytics?.totalListeners > 0)
                    .map(p => p.creatorId)
                ).size}
              </div>
              <div className="text-sm text-slate-400">Active Creators</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// CSS styles - add to your CSS file
const styles = `
.status-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  white-space: nowrap;
}

.status-success {
  background-color: rgba(34, 197, 94, 0.1);
  color: rgb(34, 197, 94);
  border: 1px solid rgba(34, 197, 94, 0.2);
}

.status-warning {
  background-color: rgba(251, 191, 36, 0.1);
  color: rgb(251, 191, 36);
  border: 1px solid rgba(251, 191, 36, 0.2);
}

.status-error {
  background-color: rgba(239, 68, 68, 0.1);
  color: rgb(239, 68, 68);
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.status-neutral {
  background-color: rgba(148, 163, 184, 0.1);
  color: rgb(148, 163, 184);
  border: 1px solid rgba(148, 163, 184, 0.2);
}
`;

export default PodcastAnalyticsPage;